/**
 * In-memory activity records and local hierarchy expansion state. Fetch snapshots combine the
 * current user and mutation revision so delayed reads cannot overwrite newer edits, deletions, or
 * another user's tasks.
 *
 * @module apps/web/src/stores/tasksStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { TaskUI, TaskStatus } from '@fluxability/types';
import { useUserStore } from './userStore';

/**
 * Identity and mutation revision captured before an asynchronous task read. Both values must still
 * match before accepting the response.
 */
export interface TaskSnapshot {
  /** Task mutation counter captured before the read. */
  revision: number;
  /** Authenticated user identity captured before the read; null when signed out. */
  userId: string | null;
}
/**
 * Capture the current task revision and user ID before starting a read whose result may replace
 * task data.
 */
export const captureTaskSnapshot = (): TaskSnapshot => ({
  revision: useTasksStore.getState().revision,
  userId: useUserStore.getState().userId,
});
/**
 * Return whether both user ID and mutation revision still match the captured read snapshot.
 */
export const isTaskSnapshotCurrent = (snapshot: TaskSnapshot) =>
  snapshot.revision === useTasksStore.getState().revision &&
  snapshot.userId === useUserStore.getState().userId;

/** In-memory activity projection, selection, and expansion state. Mutations increment revision so snapshot-aware readers can reject stale results; actions update local state and do not call the API. */
interface TasksState {
  /** Current in-memory activity records; only expansion preferences are persisted. */
  tasks: TaskUI[];
  /** Monotonic local mutation counter compared by asynchronous read snapshots. */
  revision: number;
  /** Activity currently selected in the UI, or null. */
  selectedTaskId: string | null;
  /** Hierarchy rows expanded by the user; serialized as an array in persisted preferences. */
  expandedTaskIds: Set<string>;

  // Actions
  /** Replace records after optional identity/revision validation; returns false when the snapshot is stale. */
  setTasks: (tasks: TaskUI[], snapshot?: TaskSnapshot) => boolean;
  /** Upsert one record without duplicating its ID and advance the mutation revision. */
  addTask: (task: TaskUI) => void;
  /** Apply one committed batch; realtime ignores older server timestamps, while revision-fenced authoritative replies win. */
  upsertTasks: (tasks: TaskUI[], source?: 'authoritative' | 'realtime') => void;
  /** Shallow-merge fields onto an existing record, updating its local timestamp and revision. */
  updateTask: (id: string, updates: Partial<TaskUI>) => void;
  /** Remove one local record and clear selection if necessary; this does not call the server. */
  deleteTask: (id: string) => void;
  /** Remove a set of local IDs and clean both selection and expanded-row state. */
  deleteTasks: (ids: string[]) => void;
  /** Set or clear the selected activity without changing activity data. */
  selectTask: (id: string | null) => void;
  /** Toggle one hierarchy row using a fresh Set so subscribers observe the change. */
  toggleExpanded: (id: string) => void;

  // Selectors
  /** Find the current record by ID, returning undefined if it is absent. */
  getTask: (id: string) => TaskUI | undefined;
  /** Select direct children and sort by their stored position. */
  getTasksByParent: (parentId: string | null) => TaskUI[];
  /** Select records matching the exact supplied status. */
  getTasksByStatus: (status: TaskStatus) => TaskUI[];
  /** Select unfinished, uncancelled records with neither stored placement endpoint. */
  getUnscheduledTasks: () => TaskUI[];
  /** Select activities whose start falls on the supplied browser-local civil date. */
  getScheduledTasksForDate: (date: string) => TaskUI[];
}

/**
 * Zustand activity store with deduplicating replacement, ordered upserts, revisioned mutations, and
 * hierarchy/status/local-date selectors. Pass a snapshot to setTasks to reject stale reads. Only
 * expanded task IDs are persisted under the historical flexiplanner-tasks key; activity records
 * remain in memory.
 */
export const useTasksStore = create<TasksState>()(
  persist(
    (set, get) => ({
      tasks: [],
      revision: 0,
      selectedTaskId: null,
      expandedTaskIds: new Set(),

      setTasks: (tasks, snapshot) => {
        if (snapshot && !isTaskSnapshotCurrent(snapshot)) return false;
        set(state => {
          const current = new Map(state.tasks.map(task => [task.id, task]));
          const next = dedupeTasksById(tasks).map(task => {
            const previous = current.get(task.id);
            return rememberServerTimestamp(
              previous && sameTaskRecord(previous, task) ? previous : task,
              Date.parse(task.updatedAt)
            );
          });
          // An unchanged reply keeps the list's identity, so screens reading it need not re-render.
          const unchanged =
            next.length === state.tasks.length &&
            next.every((task, index) => task === state.tasks[index]);
          return {
            tasks: unchanged ? state.tasks : next,
            revision: state.revision + 1,
          };
        });
        return true;
      },

      addTask: task => {
        set(state => ({
          tasks: upsertTaskById(state.tasks, task),
          revision: state.revision + 1,
        }));
      },

      upsertTasks: (tasks, source = 'authoritative') => {
        set(state => {
          const incoming = new Map(tasks.map(task => [task.id, task]));
          let changed = false;
          const merged = state.tasks.map(task => {
            const replacement = incoming.get(task.id);
            incoming.delete(task.id);
            if (!replacement || (source === 'realtime' && olderTaskRecord(replacement, task)))
              return task;
            if (sameTaskRecord(task, replacement)) {
              rememberServerTimestamp(task, Date.parse(replacement.updatedAt));
              return task;
            }
            changed = true;
            return rememberServerTimestamp(replacement);
          });
          if (!changed && !incoming.size) return state;
          return {
            tasks: [
              ...merged,
              ...Array.from(incoming.values(), task => rememberServerTimestamp(task)),
            ],
            revision: state.revision + 1,
          };
        });
      },

      updateTask: (id, updates) => {
        set(state => ({
          revision: state.revision + 1,
          tasks: state.tasks.map(task =>
            task.id === id
              ? preserveServerTimestamp(task, {
                  ...task,
                  ...updates,
                  updatedAt: new Date().toISOString(),
                })
              : task
          ),
        }));
      },

      deleteTask: id => {
        set(state => ({
          revision: state.revision + 1,
          tasks: state.tasks.filter(task => task.id !== id),
          selectedTaskId: state.selectedTaskId === id ? null : state.selectedTaskId,
        }));
      },

      deleteTasks: ids => {
        const deleted = new Set(ids);
        set(state => ({
          revision: state.revision + 1,
          tasks: state.tasks.filter(task => !deleted.has(task.id)),
          selectedTaskId:
            state.selectedTaskId && deleted.has(state.selectedTaskId) ? null : state.selectedTaskId,
          expandedTaskIds: new Set([...state.expandedTaskIds].filter(id => !deleted.has(id))),
        }));
      },

      selectTask: id => set({ selectedTaskId: id }),

      toggleExpanded: id => {
        set(state => {
          const newExpanded = new Set(state.expandedTaskIds);
          if (newExpanded.has(id)) {
            newExpanded.delete(id);
          } else {
            newExpanded.add(id);
          }
          return { expandedTaskIds: newExpanded };
        });
      },

      getTask: id => get().tasks.find(task => task.id === id),

      getTasksByParent: parentId => {
        return get()
          .tasks.filter(task => task.parentId === parentId)
          .sort((a, b) => a.position - b.position);
      },

      getTasksByStatus: status => {
        return get().tasks.filter(task => task.status === status);
      },

      getUnscheduledTasks: () => {
        return get().tasks.filter(
          task =>
            !task.scheduledStart &&
            !task.scheduledEnd &&
            task.status !== 'done' &&
            task.status !== 'completed' &&
            task.status !== 'cancelled'
        );
      },

      getScheduledTasksForDate: date => {
        return get().tasks.filter(task => {
          if (!task.scheduledStart) return false;
          const d = new Date(task.scheduledStart);
          const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          return localDate === date;
        });
      },
    }),
    {
      name: 'flexiplanner-tasks',
      partialize: state => ({
        expandedTaskIds: Array.from(state.expandedTaskIds),
      }),
      merge: (persisted, current) => ({
        ...current,
        expandedTaskIds: new Set(
          (persisted as { /** Expanded IDs written by partialize. */ expandedTaskIds?: string[] } | undefined)
            ?.expandedTaskIds || []
        ),
      }),
    }
  )
);

/** Replace every duplicate of an activity ID with the incoming record at its first existing position, or append a new ID. Existing relative order is retained for other activities. */
function upsertTaskById(tasks: TaskUI[], nextTask: TaskUI): TaskUI[] {
  const existingIndex = tasks.findIndex(task => task.id === nextTask.id);
  if (existingIndex === -1) return [...tasks, nextTask];

  const nextTasks = tasks.filter(task => task.id !== nextTask.id);
  nextTasks.splice(existingIndex, 0, nextTask);
  return nextTasks;
}

/** Keep one record per activity ID, preserving the first occurrence’s position but taking the last occurrence’s value. This tolerates overlapping refresh/realtime payloads without multiplying cards. */
function dedupeTasksById(tasks: TaskUI[]): TaskUI[] {
  const seen = new Set<string>();
  const deduped: TaskUI[] = [];

  for (const task of tasks) {
    if (seen.has(task.id)) {
      const existingIndex = deduped.findIndex(existing => existing.id === task.id);
      if (existingIndex !== -1) deduped[existingIndex] = task;
      continue;
    }
    seen.add(task.id);
    deduped.push(task);
  }

  return deduped;
}

/** Server-origin versions follow record identity, never user-controlled browser clocks. Weak keys release timestamps when task records are replaced or session state is cleared; no ID-indexed history or version data is persisted. */
const serverTimestamps = new WeakMap<TaskUI, number>();

/** Attach the timestamp of an authenticated snapshot/proposal/event, excluding optimistic browser clocks. */
function rememberServerTimestamp(task: TaskUI, timestamp = Date.parse(task.updatedAt)): TaskUI {
  if (Number.isFinite(timestamp)) serverTimestamps.set(task, timestamp);
  return task;
}

/** Local edits retain their last known server version independently of the optimistic display timestamp. */
function preserveServerTimestamp(previous: TaskUI, next: TaskUI): TaskUI {
  const timestamp = serverTimestamps.get(previous);
  if (timestamp !== undefined) serverTimestamps.set(next, timestamp);
  return next;
}

/** Compare delayed realtime events only with a known server-origin version, never a browser clock. */
function olderTaskRecord(incoming: TaskUI, current: TaskUI): boolean {
  const incomingTime = Date.parse(incoming.updatedAt);
  const currentTime = serverTimestamps.get(current);
  return Number.isFinite(incomingTime) && currentTime !== undefined && incomingTime < currentTime;
}

/** Preserve object identity on duplicate proposal/realtime records so unchanged blocks do not rerender. */
function sameTaskRecord(left: TaskUI, right: TaskUI): boolean {
  return left === right || JSON.stringify(left) === JSON.stringify(right);
}
